

import React, { useState, useEffect } from 'react';
import { LogoPlaceholder, LoadingSpinner } from './icons';
import { registerOrganization, getLegalContent } from '../services/apiService';
import LegalModal from './LegalModal';
import { LegalContent } from '../types';

interface RegistrationPageProps {
  onSwitchToLogin: () => void;
}

const RegistrationPage: React.FC<RegistrationPageProps> = ({ onSwitchToLogin }) => {
  const [formData, setFormData] = useState({
    name: '',
    abn: '',
    contactPerson: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
  });
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [legalContent, setLegalContent] = useState<LegalContent | null>(null);
  const [modalContent, setModalContent] = useState<{title: string, content: string} | null>(null);


  useEffect(() => {
    const fetchLegal = async () => {
        const content = await getLegalContent();
        setLegalContent(content);
    };
    fetchLegal();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
     if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long.');
      return;
    }
    if (!agreedToTerms) {
      setError('You must agree to the terms to register.');
      return;
    }
    setIsLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const { password, confirmPassword, ...orgData } = formData;
      await registerOrganization(orgData, password, agreedToTerms);
      setSuccess('Registration successful! Your account is pending approval from a system administrator.');
      setFormData({ name: '', abn: '', contactPerson: '', email: '', phone: '', password: '', confirmPassword: '' });
      setAgreedToTerms(false);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsLoading(false);
    }
  };

  const openModal = (type: 'privacy' | 'terms') => {
      if (!legalContent) return;
      if (type === 'privacy') {
          setModalContent({ title: 'Privacy Policy', content: legalContent.privacyPolicy });
      } else {
          setModalContent({ title: 'Terms and Conditions', content: legalContent.termsAndConditions });
      }
  };

  return (
    <>
    <div className="min-h-screen bg-gray-100 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <LogoPlaceholder className="mx-auto h-16 w-auto text-primary" />
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          Register a Taxi Network
        </h2>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow-xl ring-1 ring-gray-900/10 sm:rounded-lg sm:px-10">
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div>
              <label className="block text-sm font-medium text-gray-700">Network/Company Name</label>
              <input name="name" type="text" required onChange={handleChange} value={formData.name} className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-light focus:border-primary-light sm:text-sm" />
            </div>
             <div>
              <label className="block text-sm font-medium text-gray-700">ABN / Registration Number</label>
              <input name="abn" type="text" required onChange={handleChange} value={formData.abn} className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-light focus:border-primary-light sm:text-sm" />
            </div>
             <div>
              <label className="block text-sm font-medium text-gray-700">Contact Person Name</label>
              <input name="contactPerson" type="text" required onChange={handleChange} value={formData.contactPerson} className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-light focus:border-primary-light sm:text-sm" />
            </div>
             <div>
              <label className="block text-sm font-medium text-gray-700">Contact & Login Email</label>
              <input name="email" type="email" required onChange={handleChange} value={formData.email} className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-light focus:border-primary-light sm:text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Phone Number</label>
              <input name="phone" type="tel" required onChange={handleChange} value={formData.phone} className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-light focus:border-primary-light sm:text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Password</label>
              <input name="password" type="password" required onChange={handleChange} value={formData.password} className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-light focus:border-primary-light sm:text-sm" />
            </div>
             <div>
              <label className="block text-sm font-medium text-gray-700">Confirm Password</label>
              <input name="confirmPassword" type="password" required onChange={handleChange} value={formData.confirmPassword} className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-light focus:border-primary-light sm:text-sm" />
            </div>

            <div className="flex items-start">
                <div className="flex items-center h-5">
                    <input
                        id="terms-agreement"
                        name="terms-agreement"
                        type="checkbox"
                        checked={agreedToTerms}
                        onChange={(e) => setAgreedToTerms(e.target.checked)}
                        className="focus:ring-primary-light h-4 w-4 text-primary border-gray-300 rounded"
                    />
                </div>
                <div className="ml-3 text-sm">
                    <label htmlFor="terms-agreement" className="text-gray-700">
                        I have read and agree to the{' '}
                        <button type="button" onClick={() => openModal('terms')} className="font-medium underline hover:text-primary">Terms & Conditions</button> and{' '}
                        <button type="button" onClick={() => openModal('privacy')} className="font-medium underline hover:text-primary">Privacy Policy</button>.
                    </label>
                </div>
            </div>

            {error && <p className="text-sm text-red-600 p-2 bg-red-50 rounded">{error}</p>}
            {success && <p className="text-sm text-green-600 p-2 bg-green-50 rounded">{success}</p>}

            <div>
              <button
                type="submit"
                disabled={isLoading || !agreedToTerms}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-light focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-dark disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {isLoading ? <LoadingSpinner className="w-5 h-5" /> : 'Register'}
              </button>
            </div>
            <div className="text-center text-sm">
                <button type="button" onClick={onSwitchToLogin} className="font-medium text-primary hover:text-primary-dark">
                  Already have an account? Sign in
                </button>
            </div>
          </form>
        </div>
      </div>
    </div>
    {modalContent && <LegalModal title={modalContent.title} content={modalContent.content} onClose={() => setModalContent(null)} />}
    </>
  );
};

export default RegistrationPage;